import { Button } from '@shared/ui-react/Button';
import * as React from 'react';
import { observer } from 'mobx-react';
import { LongSectionStore } from '@pages/cross-section/long-section/LongSectionStore';
import { CrossSectionStore } from '@pages/cross-section/cross-section/CrossSectionStore';
import { autorun, makeObservable, observable } from 'mobx';
import { LStorage } from '@shared/Storage';
import DroneIcon from '@shared/ui-react/DroneIcon';

@observer
export class CrossSectionModelsListComponent extends React.Component<{
    store: CrossSectionStore | LongSectionStore;
    hasPipes: boolean;
    hasLayers: boolean;
}> {
    storageKey = 'CrossSection_showLayersInUI';
    @observable showLayersInUI: boolean = JSON.parse(LStorage.getItem(this.storageKey) ?? 'true');

    constructor(props: any) {
        super(props);
        makeObservable(this);

        autorun(() => {
            LStorage.setItem(this.storageKey, this.showLayersInUI);
        });
    }

    toggleShowLayers = () => {
        this.showLayersInUI = !this.showLayersInUI;
    };

    loadAllProjectModels = () => {
        window.DialogConfirm(
            {
                msg: kuura.getMessage('general.warning.slow_action'),
            },
            () => {
                this.props.store.loadSelection.includeModelIds = null;
                this.props.store.fetchDrawing();
            }
        );
    };

    loadAllProjectLogpoints = () => {
        this.props.store.loadSelection.includeLogpointIds = null;
        this.props.store.drawAll();
    };

    hideAllLogpoints = () => {
        this.props.store.getModelsControls.forEach(ctl => {
            if (ctl.isLogpoint) {
                this.props.store.hiddenLogpointsByModelId.push(ctl.modelId);
            }
        });
        this.props.store.drawAll();
    };

    toggleCheckPipe = (id: number) => {
        if (this.props.store.hiddenPipeIds.includes(id)) {
            this.props.store.hiddenPipeIds = this.props.store.hiddenPipeIds.filter(x => x !== id);
        } else {
            this.props.store.hiddenPipeIds.push(id);
        }
        this.props.store.drawAll();
    };

    toggleCheckModel = (id: number | string, children?: { modelId: string }[]) => {
        if (this.props.store.hiddenModelIds.includes(id)) {
            this.props.store.hiddenModelIds = this.props.store.hiddenModelIds.filter(x => ![id, ...(children ?? []).map(c => c.modelId)].includes(x));
        } else {
            this.props.store.hiddenModelIds.push(id);
            children?.forEach(c => this.props.store.hiddenModelIds.push(c.modelId));
        }
        this.props.store.drawAll();
    };

    toggleCheckLogpoints = (id: number) => {
        if (this.props.store.hiddenLogpointsByModelId.includes(id)) {
            this.props.store.hiddenLogpointsByModelId = this.props.store.hiddenLogpointsByModelId.filter(x => x !== id);
        } else {
            this.props.store.hiddenLogpointsByModelId.push(id);
        }
        this.props.store.drawAll();
    };

    toggleCheckPointCloud = (id: number) => {
        if (this.props.store.hiddenPointCloudIds.includes(id)) {
            this.props.store.hiddenPointCloudIds = this.props.store.hiddenPointCloudIds.filter(x => x !== id);
        } else {
            this.props.store.hiddenPointCloudIds.push(id);
        }
        this.props.store.drawAll();
    };

    toggleCheckAllModels = () => {
        if (this.props.store.hiddenModelIds.length === 0) {
            this.props.store.hiddenModelIds = Array.from(
                new Set([...this.props.store.latestServerData.models.map(x => x.modelId), ...this.props.store.latestServerData.models.flatMap(model => model.lines.map(l => l.id))])
            );
        } else {
            this.props.store.hiddenModelIds = [];
        }
        this.props.store.drawAll();
    };

    toggleCheckAllPointClouds = () => {
        if (this.props.store.hiddenPointCloudIds.length === 0) {
            this.props.store.hiddenPointCloudIds = Array.from(this.props.store.pointCloudSelection);
        } else {
            this.props.store.hiddenPointCloudIds = [];
        }
        this.props.store.drawAll();
    };

    toggleCheckAllLogpoints = () => {
        if (this.props.store.hiddenLogpointsByModelId.length === 0) {
            this.props.store.hiddenLogpointsByModelId = Array.from(new Set(this.props.store.latestServerData.logpoints.map(x => x.modelId)));
        } else {
            this.props.store.hiddenLogpointsByModelId = [];
        }
        this.props.store.drawAll();
    };

    toggleCheckAllPipes = () => {
        if (this.props.store.hiddenPipeIds.length === 0) {
            this.props.store.hiddenPipeIds = Array.from(new Set(this.props.store.latestServerData.pipes.map(x => x.modelId)));
        } else {
            this.props.store.hiddenPipeIds = [];
        }
        this.props.store.drawAll();
    };

    render() {
        const { store, hasPipes, hasLayers } = this.props;

        return (
            <div className='cross-section__models_wrapper'>
                <div style={{ marginBottom: 20 }}>
                    {store.loadSelection?.includeModelIds?.filter(id => id !== store.loadSelection?.alignmentId)?.length > 0 ? (
                        <div className='flex-row gap'>
                            <span>{__('cross_section.showing_only_included')}</span>
                            <a href='#' onClick={this.loadAllProjectModels}>
                                {__('cross_section.use_all_project_models')}
                            </a>
                        </div>
                    ) : (
                        <span>{__('cross_section.showing_all_project_models')}</span>
                    )}

                    {store.loadSelection?.includeLogpointIds?.length > 0 ? (
                        <div className='flex-row gap'>
                            <span>{__('cross_section.showing_only_visible_logpoints')}</span>
                            <a href='#' onClick={this.loadAllProjectLogpoints}>
                                {__('cross_section.use_all_project_logpoints')}
                            </a>
                        </div>
                    ) : (
                        <div className='flex-row gap'>
                            <span>{__('cross_section.showing_all_project_logpoints')}</span>
                            {/*<a href='#' onClick={this.hideAllLogpoints}>{__('general.clear')}</a>*/}
                        </div>
                    )}
                </div>

                <table>
                    <thead>
                        <tr>
                            {hasPipes && (
                                <th>
                                    <Button
                                        secondary
                                        tooltip={'Pipes: select all/none'}
                                        icon='iki iki-circle'
                                        onClick={this.toggleCheckAllPipes}
                                        disabled={!store.latestServerData?.pipes?.length}
                                    />
                                </th>
                            )}
                            <th>
                                <Button
                                    secondary
                                    tooltip={'Logpoints: select all/none'}
                                    icon='iki iki-asbuilt'
                                    onClick={this.toggleCheckAllLogpoints}
                                    disabled={!store.latestServerData?.logpoints?.length}
                                />
                            </th>
                            {store.pointCloudsIncluded && (
                                <th>
                                    <Button secondary tooltip={'Point Clouds'} onClick={this.toggleCheckAllPointClouds} disabled={!store.pointCloudSelection.size}>
                                        <DroneIcon />
                                    </Button>
                                </th>
                            )}
                            <th>
                                <Button
                                    secondary
                                    tooltip={'Models: select all/none'}
                                    icon='iki iki-cross-section'
                                    onClick={this.toggleCheckAllModels}
                                    disabled={!store.latestServerData?.models?.length}
                                />
                            </th>
                            {hasLayers && (
                                <th>
                                    <Button
                                        secondary
                                        tooltip={'Show/hide layers'}
                                        icon={this.showLayersInUI ? 'iki iki-list-collapse' : 'iki iki-list-expand'}
                                        onClick={this.toggleShowLayers}
                                    />
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {store.getModelsControls.map(m => {
                            return (
                                <React.Fragment key={m.modelId}>
                                    <tr>
                                        {hasPipes && (
                                            <td className='text-center'>
                                                {m.isPipe && (
                                                    <input type='checkbox' checked={!store.hiddenPipeIds.includes(m.modelId)} onClick={() => this.toggleCheckPipe(m.modelId)} />
                                                )}
                                            </td>
                                        )}
                                        <td className='text-center'>
                                            {m.isLogpoint && (
                                                <input
                                                    type='checkbox'
                                                    checked={!store.hiddenLogpointsByModelId.includes(m.modelId)}
                                                    onClick={() => this.toggleCheckLogpoints(m.modelId)}
                                                />
                                            )}
                                        </td>
                                        {store.pointCloudsIncluded && (
                                            <td className='text-center'>
                                                {m.isPointCloud && (
                                                    <input
                                                        type='checkbox'
                                                        checked={!store.hiddenPointCloudIds.includes(m.modelId)}
                                                        onClick={() => this.toggleCheckPointCloud(m.modelId)}
                                                    />
                                                )}
                                            </td>
                                        )}
                                        <td className='text-center'>
                                            {m.isModel && (
                                                <input
                                                    type='checkbox'
                                                    checked={!store.hiddenModelIds.includes(m.modelId)}
                                                    onClick={() => this.toggleCheckModel(m.modelId, m.children)}
                                                />
                                            )}
                                        </td>
                                        <td>
                                            <span className='break-text' style={{ color: m.color ? 'rgb(' + m.color + ')' : null }}>
                                                {m.name}
                                            </span>
                                        </td>
                                    </tr>
                                    {this.showLayersInUI &&
                                        m.children.map(child => {
                                            return (
                                                <tr key={child.modelId}>
                                                    <td></td>
                                                    <td></td>
                                                    <td></td>
                                                    <td></td>
                                                    <td>
                                                        <div className='flex-row ai-center'>
                                                            <input
                                                                type='checkbox'
                                                                checked={!store.hiddenModelIds.includes(child.modelId)}
                                                                onClick={() => this.toggleCheckModel(child.modelId)}
                                                            />
                                                            <span className='break-text' style={{ marginLeft: 5, color: child.color ? 'rgb(' + child.color + ')' : null }}>
                                                                {child.name}
                                                            </span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }
}
